import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';
import { euro } from '../../utils/formatUtils.js';
import ConfirmModal from '../../components/ConfirmModal.jsx';

// ─── Helpers ──────────────────────────────────────────────────
function duurLabel(months) {
  return t(months === 1 ? 'abonnementen.duurMaand' : 'abonnementen.duurMaanden', { aantal: months });
}

function calcPrijsExcl(total, vatRate) {
  if (!total) return 0;
  if (!vatRate) return total;
  return total / (1 + vatRate / 100);
}

function isStaffelVerstreken(s) {
  if (!s.available_until) return false;
  return s.available_until < new Date().toISOString().slice(0, 10);
}

function formatDatum(dateStr) {
  if (!dateStr) return '∞';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Staffel Modal ────────────────────────────────────────────
function StaffelModal({ typeId, bestaandeMonths, staffel, onClose, onSaved }) {
  const isNew = !staffel?.id;
  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    duration_months:      staffel?.duration_months      ?? '',
    price_per_month:      staffel?.price_per_month      ?? '',
    price_per_period:     staffel?.price_per_period     ?? '',
    vat_rate:             staffel?.vat_rate              ?? 9,
    available_from:       staffel?.available_from        ?? '',
    available_until:      staffel?.available_until       ?? '',
    payment_frequency:    staffel?.payment_frequency     ?? 'period',
    is_active:            staffel?.is_active             ?? true,
    period_discount_pct:  staffel?.period_discount_pct  ?? 0,
    promo_label:          staffel?.promo_label           ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const SUGGESTION_MONTHS = [1, 3, 6, 12];
  const suggestions = isNew ? SUGGESTION_MONTHS.filter(m => !bestaandeMonths.includes(m)) : [];

  function handlePricePerMonthChange(val) {
    const ppm = parseFloat(val) || 0;
    const months = parseInt(form.duration_months) || 0;
    const total = months > 0 ? (ppm * months).toFixed(2) : '';
    setForm(p => ({ ...p, price_per_month: val, price_per_period: total !== '' ? total : p.price_per_period }));
  }

  function handlePricePerPeriodChange(val) {
    const total = parseFloat(val) || 0;
    const months = parseInt(form.duration_months) || 0;
    const ppm = months > 0 ? (total / months).toFixed(2) : '';
    setForm(p => ({ ...p, price_per_period: val, price_per_month: ppm !== '' ? ppm : p.price_per_month }));
  }

  function handleDurationChange(val) {
    const months = parseInt(val) || 0;
    const ppm = parseFloat(form.price_per_month) || 0;
    const total = months > 0 && ppm > 0 ? (ppm * months).toFixed(2) : form.price_per_period;
    setForm(p => ({ ...p, duration_months: val, price_per_period: total }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    const months = parseInt(form.duration_months);
    if (!months || months < 1) { setError(t('abonnementen.validatieLooptijdMin')); return; }
    if (isNew && bestaandeMonths.includes(months)) { setError(t('abonnementen.staffelAlBestaat')); return; }
    if (form.price_per_month === '' || form.price_per_month === null) { setError(t('abonnementen.validatiePricePerMonth')); return; }

    setSaving(true);
    try {
      const pricePerPeriod = parseFloat(form.price_per_period) || parseFloat(form.price_per_month) * months;
      const showDiscount = ['period', 'both'].includes(form.payment_frequency);
      const payload = {
        membership_type_id:  typeId,
        duration_months:     months,
        price_per_month:     parseFloat(form.price_per_month),
        price_per_period:    pricePerPeriod,
        vat_rate:            Number(form.vat_rate),
        available_from:      form.available_from || null,
        available_until:     form.available_until || null,
        payment_frequency:   form.payment_frequency,
        is_active:           form.is_active,
        period_discount_pct: showDiscount ? (parseFloat(form.period_discount_pct) || 0) : 0,
        promo_label:         form.promo_label.trim() || null,
      };

      if (isNew) {
        const { error: insErr } = await supabase.from('membership_type_periods').insert(payload);
        if (insErr) {
          if (insErr.code === '23505') { setError(t('abonnementen.staffelAlBestaat')); setSaving(false); return; }
          throw insErr;
        }
      } else {
        const { data: updRow, error: updErr } = await supabase
          .from('membership_type_periods')
          .update(payload)
          .eq('id', staffel.id)
          .select('id')
          .maybeSingle();
        if (updErr) throw updErr;
        if (!updRow) { setError(t('common.wijzigingNietOpgeslagen')); setSaving(false); return; }
      }
      onSaved();
    } catch (err) {
      setError(t('abonnementen.staffelFoutOpslaan') + err.message);
    } finally {
      setSaving(false);
    }
  }

  const vatRate = Number(form.vat_rate);
  const priceTotal = parseFloat(form.price_per_period) || 0;
  const prijsExcl = calcPrijsExcl(priceTotal, vatRate);
  const prijsIncl = priceTotal;
  const warnVerstreken = form.available_until && form.available_until < today;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520, maxHeight: '92vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isNew ? t('abonnementen.staffelModalTitel') : t('abonnementen.staffelModalBewerken')}
          </h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}
          {warnVerstreken && (
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#92400e' }}>
              {t('abonnementen.warnVerstrekenStaffelModal')}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{t('abonnementen.labelDurationMonthsDetail')}</label>
            {isNew && suggestions.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                {suggestions.map(m => (
                  <button
                    key={m}
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ fontSize: 12, padding: '3px 10px' }}
                    onClick={() => handleDurationChange(String(m))}
                  >
                    {duurLabel(m)}
                  </button>
                ))}
              </div>
            )}
            <input
              className="form-input"
              type="number"
              min="1"
              value={form.duration_months}
              onChange={e => handleDurationChange(e.target.value)}
              disabled={!isNew}
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('abonnementen.labelPricePerMonth')}</label>
              <input
                className="form-input"
                type="number"
                min="0"
                step="0.01"
                value={form.price_per_month}
                onChange={e => handlePricePerMonthChange(e.target.value)}
                required
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('abonnementen.labelPricePerPeriod2')}</label>
              <input
                className="form-input"
                type="number"
                min="0"
                step="0.01"
                value={form.price_per_period}
                onChange={e => handlePricePerPeriodChange(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label">{t('abonnementen.labelVatRate')}</label>
            <select
              className="form-input"
              value={form.vat_rate}
              onChange={e => setForm(p => ({ ...p, vat_rate: Number(e.target.value) }))}
            >
              <option value={0}>{t('abonnementen.btwOptie0')}</option>
              <option value={9}>{t('abonnementen.btwOptie9')}</option>
              <option value={21}>{t('abonnementen.btwOptie21')}</option>
            </select>
          </div>

          {priceTotal > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12, padding: '10px 12px', background: 'var(--color-bg)', borderRadius: 6, fontSize: 13 }}>
              <div>
                <span style={{ color: 'var(--color-text-muted)' }}>{t('abonnementen.labelPriceExclBtw')}: </span>
                <strong>{euro(prijsExcl)}</strong>
              </div>
              <div>
                <span style={{ color: 'var(--color-text-muted)' }}>{t('abonnementen.labelPriceInclBtw')}: </span>
                <strong>{euro(prijsIncl)}</strong>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{t('abonnementen.labelPaymentFrequency')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
              {[
                { value: 'month',  label: t('abonnementen.payFreqMonth') },
                { value: 'period', label: t('abonnementen.payFreqPeriod') },
                { value: 'both',   label: t('abonnementen.payFreqBoth') },
              ].map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input
                    type="radio"
                    name="payment_frequency"
                    value={opt.value}
                    checked={form.payment_frequency === opt.value}
                    onChange={() => setForm(p => ({ ...p, payment_frequency: opt.value }))}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {['period', 'both'].includes(form.payment_frequency) && (
            <div className="form-group">
              <label className="form-label">{t('abonnementen.labelPeriodDiscountPct')}</label>
              <input
                className="form-input"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.period_discount_pct}
                onChange={e => setForm(p => ({ ...p, period_discount_pct: e.target.value }))}
                placeholder="0"
              />
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
                {t('abonnementen.periodDiscountHint')}
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('abonnementen.labelAvailableFrom')}</label>
              <input
                className="form-input"
                type="date"
                value={form.available_from}
                onChange={e => setForm(p => ({ ...p, available_from: e.target.value }))}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('abonnementen.labelAvailableUntil')}</label>
              <input
                className="form-input"
                type="date"
                value={form.available_until}
                onChange={e => setForm(p => ({ ...p, available_until: e.target.value }))}
              />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: 16 }}>
            <label className="form-label">{t('abonnementen.labelPromoLabel')}</label>
            <input
              className="form-input"
              type="text"
              value={form.promo_label}
              onChange={e => setForm(p => ({ ...p, promo_label: e.target.value }))}
              placeholder={t('abonnementen.promoLabelPlaceholder')}
              maxLength={80}
            />
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
              {t('abonnementen.promoLabelHint')}
            </div>
          </div>

          <div className="form-group" style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="checkbox"
              id="staffel_actief"
              checked={form.is_active}
              onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <label htmlFor="staffel_actief" className="form-label" style={{ margin: 0, cursor: 'pointer' }}>
              {t('abonnementen.labelStaffelActief')}
            </label>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('abonnementen.staffelAnnuleren')}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '…' : t('abonnementen.staffelOpslaan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Weekmodel credits (2026-08-08): parse een PostgreSQL-interval naar
// getal + eenheid voor de dropdown. NULL/leeg = volg de systeeminstelling.
function parseCreditExpiry(raw) {
  const tekst = (raw || '').trim().toLowerCase();
  if (!tekst) return null;
  const m = tekst.match(/^(\d+)\s*(mon|mons|month|months|week|weeks)$/);
  if (!m) return null;
  return { aantal: m[1], eenheid: m[2].startsWith('week') ? 'weeks' : 'mons' };
}

// ─── Type Modal ───────────────────────────────────────────────
function TypeModal({ abonnement, allTypes, onClose, onSaved, defaultSessionType, duplicateFromId }) {
  const isNew = !abonnement?.id;
  const bestaandeVerval = parseCreditExpiry(abonnement?.credit_expiry_interval);
  const [creditExpiryMode, setCreditExpiryMode] = useState(bestaandeVerval ? 'custom' : 'default');
  const [creditExpiry, setCreditExpiry] = useState(bestaandeVerval || { aantal: '3', eenheid: 'mons' });
  const [systeemVerval, setSysteemVerval] = useState(null);
  const [form, setForm] = useState({
    name:                          abonnement?.name                          || '',
    session_type:                  abonnement?.session_type                  || defaultSessionType || 'pt',
    pt_sessions_per_week:          abonnement?.pt_sessions_per_week          ?? 0,
    pt_plus_sessions_per_week:     abonnement?.pt_plus_sessions_per_week     ?? 0,
    small_group_sessions_per_week: abonnement?.small_group_sessions_per_week ?? 0,
    session_duration_minutes:      abonnement?.session_duration_minutes      ?? null,
    is_active:                     abonnement?.is_active                     ?? true,
    agreement_text:                abonnement?.agreement_text                || '',
    description:                   abonnement?.description                   || '',
    client_selectable:             abonnement?.client_selectable             ?? true,
    is_recommended:                abonnement?.is_recommended                ?? false,
    self_logging_default:          abonnement?.self_logging_default          ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [bonussen, setBonussen] = useState([]);
  const [bonusLoading, setBonusLoading] = useState(false);

  useEffect(() => {
    if (abonnement?.id) loadBonussen();
  }, [abonnement?.id]);

  // Systeemdefault ophalen zodat "volg systeeminstelling" de actuele waarde
  // toont — NULL moet zichtbaar iets anders zijn dan 0.
  useEffect(() => {
    let actief = true;
    (async () => {
      const { data } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'credit_default_expiry_interval')
        .maybeSingle();
      if (actief) setSysteemVerval(parseCreditExpiry(data?.value) || { aantal: '3', eenheid: 'mons' });
    })();
    return () => { actief = false; };
  }, []);

  function vervalLabel(v) {
    if (!v) return '';
    return v.eenheid === 'weeks'
      ? t('abonnementen.creditVervalWeken', { aantal: v.aantal })
      : t('abonnementen.creditVervalMaanden', { aantal: v.aantal });
  }

  async function loadBonussen() {
    setBonusLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('membership_type_bonuses')
        .select('id, bonus_type_id, bonus_price_override, is_active')
        .eq('membership_type_id', abonnement.id)
        .order('created_at');
      if (err) throw err;
      setBonussen(data || []);
    } catch (err) {
      setError(t('abonnementen.bonusFoutLaden') + err.message);
    } finally {
      setBonusLoading(false);
    }
  }

  async function handleAddBonus(bonusTypeId) {
    try {
      const { error: err } = await supabase.from('membership_type_bonuses').insert({
        membership_type_id: abonnement.id,
        bonus_type_id: bonusTypeId,
      });
      if (err) throw err;
      await loadBonussen();
    } catch (err) {
      setError(t('abonnementen.bonusFoutOpslaan') + err.message);
    }
  }

  async function handleUpdateBonusPrice(bonusId, val) {
    const price = val === '' ? null : parseFloat(val);
    try {
      const { data: updRow, error: err } = await supabase
        .from('membership_type_bonuses')
        .update({ bonus_price_override: price })
        .eq('id', bonusId)
        .select('id')
        .maybeSingle();
      if (err) throw err;
      if (!updRow) { setError(t('common.wijzigingNietOpgeslagen')); return; }
      setBonussen(prev => prev.map(b => b.id === bonusId ? { ...b, bonus_price_override: price } : b));
    } catch (err) {
      setError(t('abonnementen.bonusFoutOpslaan') + err.message);
    }
  }

  async function handleDeleteBonus(bonusId) {
    try {
      const { data: delRow, error: err } = await supabase
        .from('membership_type_bonuses')
        .delete()
        .eq('id', bonusId)
        .select('id')
        .maybeSingle();
      if (err) throw err;
      if (!delRow) { setError(t('common.wijzigingNietVerwijderd')); return; }
      setBonussen(prev => prev.filter(b => b.id !== bonusId));
    } catch (err) {
      setError(t('abonnementen.bonusFoutOpslaan') + err.message);
    }
  }

  function getSessionsPerWeek() {
    if (form.session_type === 'pt') return parseInt(form.pt_sessions_per_week) || 0;
    if (form.session_type === 'pt_plus') return parseInt(form.pt_plus_sessions_per_week) || 0;
    if (form.session_type === 'small_group') return parseInt(form.small_group_sessions_per_week) || 0;
    return 0;
  }

  function getCreditsPerMonth() {
    return Math.ceil(getSessionsPerWeek() * 4.33);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError(t('abonnementen.validatieNaam')); return; }
    if (getSessionsPerWeek() < 1) { setError(t('abonnementen.validatieSessies')); return; }
    if (form.session_type === 'pt_plus' && !form.session_duration_minutes) { setError(t('abonnementen.validatieSessieDuur')); return; }
    if (creditExpiryMode === 'custom') {
      const n = parseInt(creditExpiry.aantal, 10);
      if (!Number.isInteger(n) || n < 1) { setError(t('abonnementen.validatieCreditVerval')); return; }
    }

    setSaving(true);
    try {
      const sessionsPerWeek = getSessionsPerWeek();
      const payload = {
        name:                          form.name.trim(),
        session_type:                  form.session_type,
        pt_sessions_per_week:          form.session_type === 'pt' ? (parseInt(form.pt_sessions_per_week) || 0) : 0,
        pt_plus_sessions_per_week:     form.session_type === 'pt_plus' ? (parseInt(form.pt_plus_sessions_per_week) || 0) : 0,
        small_group_sessions_per_week: form.session_type === 'small_group' ? (parseInt(form.small_group_sessions_per_week) || 0) : 0,
        session_duration_minutes:      form.session_type === 'pt_plus' ? parseInt(form.session_duration_minutes) : null,
        legacy_credits_per_period:     Math.ceil(sessionsPerWeek * 4.33),
        is_active:                     form.is_active,
        agreement_text:                form.agreement_text.trim() || null,
        description:                   form.description.trim() || null,
        client_selectable:             form.client_selectable,
        is_recommended:                form.is_recommended,
        self_logging_default:          form.self_logging_default,
        // Weekmodel 2026-08-08: NULL = volg settings.credit_default_expiry_interval.
        credit_expiry_interval:        creditExpiryMode === 'custom'
          ? `${parseInt(creditExpiry.aantal, 10)} ${creditExpiry.eenheid}`
          : null,
      };

      if (isNew) {
        const { data: inserted, error: insErr } = await supabase
          .from('membership_types')
          .insert(payload)
          .select('id')
          .maybeSingle();
        if (insErr) throw insErr;
        if (duplicateFromId && inserted?.id) {
          const [{ data: periods, error: periodsErr }, { data: bonuses, error: bonusesErr }] = await Promise.all([
            supabase.from('membership_type_periods').select('duration_months, price_per_month, price_per_period, vat_rate, available_from, available_until, payment_frequency, period_discount_pct, is_active, promo_label').eq('membership_type_id', duplicateFromId),
            supabase.from('membership_type_bonuses').select('bonus_type_id, bonus_price_override, is_active').eq('membership_type_id', duplicateFromId),
          ]);
          if (periodsErr) throw periodsErr;
          if (bonusesErr) throw bonusesErr;
          if (periods?.length) {
            const { error: copyPeriodsErr } = await supabase.from('membership_type_periods').insert(
              periods.map(period => ({ ...period, membership_type_id: inserted.id }))
            );
            if (copyPeriodsErr) throw copyPeriodsErr;
          }
          if (bonuses?.length) {
            const { error: copyBonusesErr } = await supabase.from('membership_type_bonuses').insert(
              bonuses.map(bonus => ({ ...bonus, membership_type_id: inserted.id }))
            );
            if (copyBonusesErr) throw copyBonusesErr;
          }
        }
      } else {
        const { data: updRow, error: updErr } = await supabase
          .from('membership_types')
          .update(payload)
          .eq('id', abonnement.id)
          .select('id')
          .maybeSingle();
        if (updErr) throw updErr;
        if (!updRow) { setError(t('common.wijzigingNietOpgeslagen')); return; }
      }
      onSaved();
    } catch (err) {
      setError(t('abonnementen.foutOpslaan') + err.message);
    } finally {
      setSaving(false);
    }
  }

  const creditsPerMonth = getCreditsPerMonth();

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 700, maxHeight: '92vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">
            {duplicateFromId ? t('abonnementen.typeModalTitelDupliceren') : isNew ? t('abonnementen.typeModalTitelNieuw') : t('abonnementen.typeModalTitelBewerken')}
          </h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

          <div className="form-group">
            <label className="form-label">{t('abonnementen.labelNaam')}</label>
            <input
              className="form-input"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('abonnementen.labelDescription')}</label>
            <textarea
              className="form-input"
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder={t('abonnementen.descriptionPlaceholder')}
              rows={2}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('abonnementen.labelSessionType')}</label>
            <select
              className="form-input"
              value={form.session_type}
              onChange={e => setForm(p => ({ ...p, session_type: e.target.value }))}
            >
              <option value="pt">{t('abonnementen.sessionTypePt')}</option>
              <option value="pt_plus">{t('abonnementen.sessionTypePtPlus')}</option>
              <option value="small_group">{t('abonnementen.sessionTypeSmallGroup')}</option>
            </select>
          </div>

          {form.session_type === 'pt' && (
            <div className="form-group">
              <label className="form-label">{t('abonnementen.labelPtSessionsPerWeek')}</label>
              <input
                className="form-input"
                type="number"
                min="1"
                value={form.pt_sessions_per_week}
                onChange={e => setForm(p => ({ ...p, pt_sessions_per_week: e.target.value }))}
                required
              />
            </div>
          )}

          {form.session_type === 'pt_plus' && (
            <>
              <div className="form-group">
                <label className="form-label">{t('abonnementen.labelPtPlusSessionsPerWeek')}</label>
                <input
                  className="form-input"
                  type="number"
                  min="1"
                  value={form.pt_plus_sessions_per_week}
                  onChange={e => setForm(p => ({ ...p, pt_plus_sessions_per_week: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('abonnementen.labelSessionDuration')}</label>
                <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                    <input
                      type="radio"
                      name="session_duration"
                      value="30"
                      checked={form.session_duration_minutes === 30 || form.session_duration_minutes === '30'}
                      onChange={() => setForm(p => ({ ...p, session_duration_minutes: 30 }))}
                    />
                    30 min
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                    <input
                      type="radio"
                      name="session_duration"
                      value="60"
                      checked={form.session_duration_minutes === 60 || form.session_duration_minutes === '60'}
                      onChange={() => setForm(p => ({ ...p, session_duration_minutes: 60 }))}
                    />
                    60 min
                  </label>
                </div>
              </div>
            </>
          )}

          {form.session_type === 'small_group' && (
            <div className="form-group">
              <label className="form-label">{t('abonnementen.labelSmallGroupSessionsPerWeek')}</label>
              <input
                className="form-input"
                type="number"
                min="1"
                value={form.small_group_sessions_per_week}
                onChange={e => setForm(p => ({ ...p, small_group_sessions_per_week: e.target.value }))}
                required
              />
            </div>
          )}

          {getSessionsPerWeek() > 0 && (
            <div style={{
              padding: '10px 14px',
              background: 'var(--color-bg)',
              borderRadius: 6,
              fontSize: 13,
              color: 'var(--color-text-muted)',
              marginBottom: 16,
            }}>
              {/* Weekmodel 2026-08-08: het weekbedrag is voortaan het
                  toekenningsbedrag. Het maandgetal blijft alleen als
                  informatieve snapshot (legacy_credits_per_period) bestaan. */}
              = {t('abonnementen.creditsPerWeekLabel', { aantal: getSessionsPerWeek() })}
              <span style={{ opacity: 0.7 }}> · {t('abonnementen.creditsPerMaandInfo', { aantal: creditsPerMonth })}</span>
            </div>
          )}

          {/* Weekmodel 2026-08-08: vervaltermijn per abonnementstype.
              NULL = volg de systeeminstelling; expliciet zichtbaar gemaakt
              zodat "geen override" niet met "0" verward kan worden. */}
          <div className="form-group">
            <label className="form-label">{t('abonnementen.creditVervalLabel')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                <input
                  type="radio"
                  name="credit_expiry_mode"
                  checked={creditExpiryMode === 'default'}
                  onChange={() => setCreditExpiryMode('default')}
                />
                {systeemVerval
                  ? t('abonnementen.creditVervalVolgSysteem', { waarde: vervalLabel(systeemVerval) })
                  : t('abonnementen.creditVervalVolgSysteemLaden')}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                <input
                  type="radio"
                  name="credit_expiry_mode"
                  checked={creditExpiryMode === 'custom'}
                  onChange={() => setCreditExpiryMode('custom')}
                />
                {t('abonnementen.creditVervalEigen')}
              </label>
              {creditExpiryMode === 'custom' && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginLeft: 26 }}>
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    max="60"
                    value={creditExpiry.aantal}
                    onChange={e => setCreditExpiry(p => ({ ...p, aantal: e.target.value }))}
                    style={{ width: 100 }}
                  />
                  <select
                    className="form-input"
                    aria-label={t('abonnementen.creditVervalEenheid')}
                    value={creditExpiry.eenheid}
                    onChange={e => setCreditExpiry(p => ({ ...p, eenheid: e.target.value }))}
                    style={{ width: 160 }}
                  >
                    <option value="weeks">{t('abonnementen.creditVervalEenheidWeken')}</option>
                    <option value="mons">{t('abonnementen.creditVervalEenheidMaanden')}</option>
                  </select>
                </div>
              )}
            </div>
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 6, lineHeight: 1.5 }}>
              {t('abonnementen.creditVervalHint')}
            </p>
          </div>

          <div className="form-group" style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="checkbox"
              id="type_actief"
              checked={form.is_active}
              onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <label htmlFor="type_actief" className="form-label" style={{ margin: 0, cursor: 'pointer' }}>
              {t('abonnementen.labelActief')}
            </label>
          </div>

          <div className="form-group" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="checkbox"
              id="type_client_selectable"
              checked={form.client_selectable}
              onChange={e => setForm(p => ({ ...p, client_selectable: e.target.checked }))}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <label htmlFor="type_client_selectable" className="form-label" style={{ margin: 0, cursor: 'pointer' }}>
              {t('abonnementen.labelClientSelectable')}
            </label>
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4, marginLeft: 26 }}>
            {t('abonnementen.clientSelectableHint')}
          </div>

          <div className="form-group" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="checkbox"
              id="type_is_recommended"
              checked={form.is_recommended}
              onChange={e => setForm(p => ({ ...p, is_recommended: e.target.checked }))}
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1d9e75' }}
            />
            <label htmlFor="type_is_recommended" className="form-label" style={{ margin: 0, cursor: 'pointer' }}>
              {t('abonnementen.labelIsRecommended')}
            </label>
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4, marginLeft: 26 }}>
            {t('abonnementen.isRecommendedHint')}
          </div>

          <div className="form-group" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="checkbox"
              id="type_self_logging_default"
              checked={form.self_logging_default}
              onChange={e => setForm(p => ({ ...p, self_logging_default: e.target.checked }))}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <label htmlFor="type_self_logging_default" className="form-label" style={{ margin: 0, cursor: 'pointer' }}>
              {t('abonnementen.labelSelfLoggingDefault')}
            </label>
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4, marginLeft: 26 }}>
            {t('abonnementen.selfLoggingDefaultHint')}
          </div>

          {/* ── Bonus sectie (alleen bij bewerken) ── */}
          {!isNew && (
            <div style={{ marginTop: 20, padding: '16px', background: 'var(--color-bg)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
              <label className="form-label" style={{ marginBottom: 12, display: 'block' }}>{t('abonnementen.sectionBonussen')}</label>
              {bonusLoading ? (
                <div style={{ padding: 8 }}><div className="spinner" style={{ width: 18, height: 18 }} /></div>
              ) : (
                <>
                  {bonussen.map(b => {
                    const bonusType = (allTypes || []).find(at => at.id === b.bonus_type_id);
                    const isBetaald = b.bonus_price_override != null && b.bonus_price_override > 0;
                    const gedragLabel = isBetaald
                      ? t('abonnementen.bonusBetaaldLabel')
                      : t('abonnementen.bonusGratisLabel');
                    return (
                      <div key={b.id} style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                          <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{bonusType?.name || '(onbekend)'}</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder={t('abonnementen.bonusPrijsHint')}
                            value={b.bonus_price_override ?? ''}
                            onChange={e => handleUpdateBonusPrice(b.id, e.target.value)}
                            style={{ width: 120, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border)', fontSize: 13 }}
                          />
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            style={{ color: 'var(--color-danger)', fontSize: 12 }}
                            onClick={() => handleDeleteBonus(b.id)}
                          >
                            {t('abonnementen.bonusVerwijderen')}
                          </button>
                        </div>
                        <div style={{ fontSize: 11, color: isBetaald ? '#92400e' : '#15803d', marginLeft: 0 }}>
                          {gedragLabel}
                        </div>
                      </div>
                    );
                  })}
                  {(() => {
                    const beschikbaar = (allTypes || []).filter(at =>
                      at.id !== abonnement.id && !bonussen.some(b => b.bonus_type_id === at.id)
                    );
                    if (beschikbaar.length === 0 && bonussen.length > 0) return null;
                    return (
                      <div style={{ marginTop: 8 }}>
                        <select
                          className="form-input"
                          style={{ fontSize: 13, maxWidth: 300 }}
                          value=""
                          onChange={e => { if (e.target.value) handleAddBonus(e.target.value); }}
                        >
                          <option value="">{beschikbaar.length > 0 ? t('abonnementen.bonusToevoegen') : t('abonnementen.bonusGeenTypes')}</option>
                          {beschikbaar.map(at => (
                            <option key={at.id} value={at.id}>{at.name}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}

          <div className="form-group" style={{ marginTop: 20 }}>
            <label className="form-label">{t('abonnementen.labelOvereenkomst')}</label>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
              {t('abonnementen.overeenkomstHint')}
            </div>
            <div style={{
              display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8,
              padding: 8, backgroundColor: 'var(--color-bg)', borderRadius: 6,
              fontSize: 11, color: 'var(--color-text-muted)',
            }}>
              <span><code>[naam]</code></span>
              <span><code>[abonnement]</code></span>
              <span><code>[prijs]</code></span>
              <span><code>[looptijd]</code></span>
              <span><code>[datum]</code></span>
              <span><code>[bedrijfsnaam]</code></span>
            </div>
            <textarea
              className="form-input"
              value={form.agreement_text}
              onChange={e => setForm(p => ({ ...p, agreement_text: e.target.value }))}
              placeholder={t('abonnementen.overeenkomstPlaceholder')}
              rows={10}
              style={{ fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('abonnementen.annuleren')}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '…' : t('abonnementen.opslaan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Betaalfrequentie badge ───────────────────────────────────
function PayFreqBadge({ freq }) {
  const labels = { month: t('abonnementen.betaalfrequentieMaand'), period: t('abonnementen.betaalfrequentiePeriode'), both: t('abonnementen.betaalfrequentieBeide') };
  const colors = { month: '#dbeafe', period: '#dcfce7', both: '#f3e8ff' };
  const textColors = { month: '#1d4ed8', period: '#15803d', both: '#7e22ce' };
  return (
    <span style={{
      background: colors[freq] || '#f3f4f6',
      color: textColors[freq] || '#374151',
      borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 600,
    }}>
      {labels[freq] || freq}
    </span>
  );
}

// ─── Staffel tabel (kaart-body) ───────────────────────────────
function StaffelTabel({ zichtbaar, onBewerken, onToggle }) {
  const thStyle = {
    textAlign: 'left',
    fontWeight: 600,
    padding: '8px 12px',
    background: 'var(--color-bg)',
    borderBottom: '1px solid var(--color-border)',
    whiteSpace: 'nowrap',
    fontSize: 11,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };
  const tdStyle = { padding: '10px 12px', whiteSpace: 'nowrap' };

  return (
    <div style={{
      overflowX: 'auto',
      marginBottom: 12,
    }}>
      <div style={{
        borderRadius: 'var(--radius)',
        border: '1px solid var(--color-border)',
        overflow: 'hidden',
        background: 'var(--color-surface)',
      }}>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>{t('abonnementen.colStaffelDuur')}</th>
              <th style={thStyle}>{t('abonnementen.colKlantprijsIncl')}</th>
              <th style={thStyle}>{t('abonnementen.colTotaal')}</th>
              <th style={thStyle}>{t('abonnementen.labelVatRate')}</th>
              <th style={thStyle}>{t('abonnementen.colExclBtw')}</th>
              <th style={thStyle}>{t('abonnementen.colBetaalfreq')}</th>
              <th style={thStyle}>{t('abonnementen.colKorting')}</th>
              <th style={thStyle}>{t('abonnementen.colPromo')}</th>
              <th style={thStyle}>{t('abonnementen.colBeschikbaar')}</th>
              <th style={thStyle}>{t('abonnementen.colStaffelStatus')}</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>{t('abonnementen.colStaffelActies')}</th>
            </tr>
          </thead>
          <tbody>
            {zichtbaar.map((s, idx) => {
              const verstreken = isStaffelVerstreken(s);
              const vatRate = s.vat_rate ?? 0;
              const exclBtw = calcPrijsExcl(s.price_per_period, vatRate);
              const isLast = idx === zichtbaar.length - 1;
              const rowBorderStyle = isLast ? 'none' : '1px solid var(--color-border)';
              const rowOpacity = !s.is_active || verstreken ? 0.5 : 1;
              const rowBg = verstreken ? '#f5f5f5' : undefined;
              return (
                <tr key={s.id} style={{ opacity: rowOpacity, background: rowBg }}>
                  <td style={{ ...tdStyle, borderBottom: rowBorderStyle }}>
                    {duurLabel(s.duration_months)}
                    {verstreken && (
                      <span
                        title={t('abonnementen.verstrekenTooltip')}
                        style={{
                          marginLeft: 6,
                          fontSize: 10,
                          background: '#fee2e2',
                          color: '#dc2626',
                          fontWeight: 700,
                          borderRadius: 4,
                          padding: '2px 6px',
                          cursor: 'help',
                          display: 'inline-block',
                        }}
                      >
                        {t('abonnementen.verstrekenLabel')}
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, borderBottom: rowBorderStyle }}>
                    <strong>{euro(s.price_per_month || 0)}{t('abonnementen.perMaand')}</strong>
                  </td>
                  <td style={{ ...tdStyle, borderBottom: rowBorderStyle }}>
                    {euro(s.price_per_period)}
                  </td>
                  <td style={{ ...tdStyle, borderBottom: rowBorderStyle }}>
                    {vatRate}%
                  </td>
                  <td style={{ ...tdStyle, borderBottom: rowBorderStyle }}>
                    {euro(exclBtw)}
                  </td>
                  <td style={{ ...tdStyle, borderBottom: rowBorderStyle }}>
                    <PayFreqBadge freq={s.payment_frequency} />
                  </td>
                  <td style={{ ...tdStyle, borderBottom: rowBorderStyle }}>
                    {['period', 'both'].includes(s.payment_frequency) && Number(s.period_discount_pct) > 0
                      ? <span style={{ color: '#15803d', fontWeight: 600 }}>{Number(s.period_discount_pct)}%</span>
                      : <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                    }
                  </td>
                  <td style={{ ...tdStyle, borderBottom: rowBorderStyle }}>
                    {s.promo_label
                      ? <span style={{ background: '#dcfce7', color: '#15803d', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{s.promo_label}</span>
                      : <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                    }
                  </td>
                  <td style={{ ...tdStyle, borderBottom: rowBorderStyle }}>
                    {s.available_from || s.available_until ? (
                      <span style={{ fontSize: 11 }}>
                        {formatDatum(s.available_from)} — {formatDatum(s.available_until)}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                        {t('abonnementen.altijdBeschikbaar')}
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, borderBottom: rowBorderStyle }}>
                    <span className={`badge ${s.is_active ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 10 }}>
                      {s.is_active ? t('abonnementen.badgeActief') : t('abonnementen.badgeInactief')}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, borderBottom: rowBorderStyle }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: 12, padding: '2px 8px' }}
                        onClick={() => onBewerken(s)}
                      >
                        {t('clientDetail.bewerken')}
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{
                          fontSize: 12,
                          padding: '2px 8px',
                          color: s.is_active ? 'var(--color-danger)' : 'var(--color-primary)',
                        }}
                        onClick={() => onToggle(s)}
                      >
                        {s.is_active ? t('abonnementen.staffelDeactiveren') : t('abonnementen.staffelActiveren')}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Staffels per type (uitklapbaar) ─────────────────────────
function StaffelsRij({ type, toonInactief, allTypes, summaryPeriods = [], activeMembers = 0, onDuplicate, onToggleType }) {
  const [staffels, setStaffels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [staffelModal, setStaffelModal] = useState(null);

  async function loadStaffels() {
    if (!open) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('membership_type_periods')
        .select('id, duration_months, price_per_month, price_per_period, vat_rate, available_from, available_until, payment_frequency, period_discount_pct, is_active, promo_label')
        .eq('membership_type_id', type.id)
        .order('duration_months');
      if (err) throw err;
      setStaffels(data || []);
    } catch (err) {
      setError(t('abonnementen.staffelFoutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadStaffels(); }, [open]);

  async function handleToggleStaffel(s) {
    try {
      const { data: updRow, error: updErr } = await supabase
        .from('membership_type_periods')
        .update({ is_active: !s.is_active })
        .eq('id', s.id)
        .select('id')
        .maybeSingle();
      if (updErr) throw updErr;
      if (!updRow) { setError(t('common.wijzigingNietOpgeslagen')); return; }
      setStaffels(prev => prev.map(x => x.id === s.id ? { ...x, is_active: !s.is_active } : x));
    } catch (err) {
      setError(t('abonnementen.staffelFoutOpslaan') + err.message);
    }
  }

  const zichtbaar = toonInactief ? staffels : staffels.filter(s => s.is_active);
  const today = new Date().toISOString().slice(0, 10);
  const bestaandeMonths = staffels
    .filter(s => s.is_active && (!s.available_until || s.available_until >= today))
    .map(s => s.duration_months);
  const activeSummaryPeriods = summaryPeriods.filter(s => s.is_active && !isStaffelVerstreken(s));
  const summaryPrices = activeSummaryPeriods.map(s => Number(s.price_per_month || 0)).filter(price => price > 0);
  const fromPrice = summaryPrices.length ? Math.min(...summaryPrices) : null;
  const warnings = [
    !type.agreement_text?.trim() ? t('abonnementen.waarschuwingGeenOvereenkomst') : null,
    activeSummaryPeriods.length === 0 ? t('abonnementen.waarschuwingGeenPrijs') : null,
  ].filter(Boolean);
  const expiryLabel = type.credit_expiry_interval
    ? type.credit_expiry_interval.replace('mons', t('abonnementen.maandenKort')).replace('weeks', t('abonnementen.wekenKort'))
    : t('abonnementen.systeemstandaard');

  const modals = (
    <>
      {staffelModal !== null && !staffelModal._isTypeEdit && (
        <StaffelModal
          typeId={type.id}
          bestaandeMonths={bestaandeMonths}
          staffel={staffelModal?.id ? staffelModal : null}
          onClose={() => setStaffelModal(null)}
          onSaved={() => { setStaffelModal(null); loadStaffels(); }}
        />
      )}
      {staffelModal?._isTypeEdit && (
        <TypeModal
          abonnement={type}
          allTypes={allTypes}
          onClose={() => setStaffelModal(null)}
          onSaved={() => { setStaffelModal(null); window.location.reload(); }}
        />
      )}
    </>
  );

  // ── INGEKLAPT: gewone tabelrij ──────────────────────────────
  if (!open) {
    return (
      <>
        <tr style={!type.is_active ? { opacity: 0.5 } : {}}>
          <td>
            <button
              className="btn btn-ghost btn-sm"
              style={{ padding: '2px 6px', marginRight: 6, fontWeight: 700, fontSize: 14, minWidth: 24 }}
              onClick={() => setOpen(true)}
              title={t('abonnementen.uitklappen')}
            >
              ▶
            </button>
            <strong>{type.name}</strong>
            {type.description && (
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2, marginLeft: 30 }}>
                {type.description}
              </div>
            )}
          </td>
          <td>
            <strong>{type.sessions_per_week_frequency || 0}×</strong> {t('abonnementen.perWeek')}
            <div className="membership-row-meta">{t('abonnementen.creditsPerWeekLabel', { aantal: type.sessions_per_week_frequency || 0 })} · {expiryLabel}</div>
          </td>
          <td>
            {fromPrice ? <strong>{t('abonnementen.vanafPrijs', { prijs: euro(fromPrice) })}</strong> : <span className="text-muted">—</span>}
            <div className="membership-row-meta">{activeMembers} {t(activeMembers === 1 ? 'abonnementen.actiefLid' : 'abonnementen.actieveLeden')}</div>
          </td>
          <td>
            <span className={`badge ${type.is_active ? 'badge-active' : 'badge-inactive'}`}>
              {type.is_active ? t('abonnementen.badgeActief') : t('abonnementen.badgeInactief')}
            </span>
            <div className="membership-row-flags">
              <span>{type.client_selectable ? t('abonnementen.zichtbaarVoorKlanten') : t('abonnementen.alleenViaCoach')}</span>
              {type.is_recommended && <span>{t('abonnementen.aanbevolenKort')}</span>}
            </div>
            {warnings.length > 0 && <div className="membership-warning" title={warnings.join(' · ')}>⚠ {t(warnings.length === 1 ? 'abonnementen.aandachtNodigEen' : 'abonnementen.aandachtNodigMeerdere', { aantal: warnings.length })}</div>}
          </td>
          <td>
            <details className="membership-actions">
              <summary aria-label={t('abonnementen.actiesVoor', { naam: type.name })}>•••</summary>
              <div>
                <button type="button" onClick={() => { setOpen(true); setStaffelModal({ _isTypeEdit: true }); }}>{t('clientDetail.bewerken')}</button>
                <button type="button" onClick={() => onDuplicate(type)}>{t('abonnementen.dupliceren')}</button>
                <button type="button" className={type.is_active ? 'is-danger' : ''} onClick={() => onToggleType(type)}>
                  {type.is_active ? t('abonnementen.deactiveren') : t('abonnementen.activeren')}
                </button>
              </div>
            </details>
          </td>
        </tr>
        {modals}
      </>
    );
  }

  // ── UITGEKLAPT: kaart-in-kaart layout ──────────────────────
  return (
    <>
      <tr>
        <td
          colSpan={5}
          style={{ padding: '8px', background: 'var(--color-bg)', borderBottom: 'none' }}
        >
          <div
            className="card"
            style={{
              padding: 0,
              overflow: 'hidden',
              opacity: type.is_active ? 1 : 0.65,
              animation: 'fadeInCard 0.18s ease',
            }}
          >
            {/* ── Kaart header ── */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 10,
              padding: '12px 16px',
              background: 'var(--color-primary-light)',
              borderBottom: '1px solid var(--color-border)',
            }}>
              <button
                className="btn btn-ghost btn-sm"
                style={{
                  padding: '2px 8px',
                  fontWeight: 700,
                  fontSize: 14,
                  background: 'rgba(255,255,255,0.65)',
                  borderColor: 'var(--color-border)',
                  minWidth: 28,
                }}
                onClick={() => setOpen(false)}
                title={t('abonnementen.inklappen')}
              >
                ▲
              </button>

              <strong style={{ fontSize: 15, color: 'var(--color-text)' }}>{type.name}</strong>

              {type.description && (
                <span style={{ fontSize: 12, color: 'var(--color-primary-dark)', opacity: 0.75 }}>
                  {type.description}
                </span>
              )}

              <span style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 4,
                padding: '2px 8px',
                fontSize: 12,
                color: 'var(--color-text-muted)',
                whiteSpace: 'nowrap',
              }}>
                {type.sessions_per_week_frequency || 0}×/wk — {Math.ceil((type.sessions_per_week_frequency || 0) * 4.33)} cr/mnd
              </span>

              <span style={{
                background: type.session_type === 'pt' ? '#dbeafe' : type.session_type === 'pt_plus' ? '#f3e8ff' : '#dcfce7',
                color: type.session_type === 'pt' ? '#1d4ed8' : type.session_type === 'pt_plus' ? '#7e22ce' : '#15803d',
                borderRadius: 4,
                padding: '2px 8px',
                fontSize: 11,
                fontWeight: 600,
              }}>
                {type.session_type === 'pt' ? 'PT' : type.session_type === 'pt_plus' ? 'PT+' : 'Small Group'}
              </span>

              <span className={`badge ${type.is_active ? 'badge-active' : 'badge-inactive'}`}>
                {type.is_active ? t('abonnementen.badgeActief') : t('abonnementen.badgeInactief')}
              </span>

              <div style={{ marginLeft: 'auto' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ background: 'rgba(255,255,255,0.65)', borderColor: 'var(--color-border)' }}
                  onClick={() => setStaffelModal({ _isTypeEdit: true })}
                >
                  {t('clientDetail.bewerken')}
                </button>
              </div>
            </div>

            {/* ── Kaart body ── */}
            <div style={{ padding: '16px', background: 'var(--color-bg)' }}>
              {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

              {loading ? (
                <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="spinner" style={{ width: 20, height: 20 }} />
                </div>
              ) : (
                <>
                  {zichtbaar.length === 0 ? (
                    <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 12 }}>
                      {t('abonnementen.geenStaffels')}
                    </p>
                  ) : (
                    <StaffelTabel
                      zichtbaar={zichtbaar}
                      onBewerken={s => setStaffelModal(s)}
                      onToggle={handleToggleStaffel}
                    />
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => setStaffelModal({})}>
                      {t('abonnementen.staffelToevoegen')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </td>
      </tr>
      {modals}
    </>
  );
}

// ─── Sessietype configuratie ──────────────────────────────────
const SESSION_SECTIONS = [
  {
    key: 'pt',
    label: () => t('abonnementen.sectionPt'),
    hint: () => t('abonnementen.sectionPtHint'),
    badge: '1:1',
    bg: '#eff6ff',
    color: '#1d4ed8',
    border: '#bfdbfe',
  },
  {
    key: 'pt_plus',
    label: () => t('abonnementen.sectionPtPlus'),
    hint: () => t('abonnementen.sectionPtPlusHint'),
    badge: '1:2',
    bg: '#faf5ff',
    color: '#7e22ce',
    border: '#e9d5ff',
  },
  {
    key: 'small_group',
    label: () => t('abonnementen.sectionSmallGroup'),
    hint: () => t('abonnementen.sectionSmallGroupHint'),
    badge: 'SG',
    bg: '#f0fdf4',
    color: '#15803d',
    border: '#bbf7d0',
  },
];

// ─── Hoofdpagina ─────────────────────────────────────────────
export default function AbonnementenPage() {
  const [abonnementen, setAbonnementen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toonInactief, setToonInactief] = useState(false);
  const [typeModal, setTypeModal] = useState(null);
  const [search, setSearch] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [collapsedSections, setCollapsedSections] = useState({});
  const [periodsByType, setPeriodsByType] = useState({});
  const [activeMemberCounts, setActiveMemberCounts] = useState({});
  const [toggleConfirm, setToggleConfirm] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('membership_types')
        .select('id, name, description, legacy_credits_per_period, session_type, pt_sessions_per_week, pt_plus_sessions_per_week, small_group_sessions_per_week, session_duration_minutes, sessions_per_week_frequency, is_active, agreement_text, client_selectable, is_recommended, credit_expiry_interval, self_logging_default')
        .order('name');
      if (err) throw err;
      setAbonnementen(data || []);
      const [{ data: periods, error: periodsErr }, { data: memberships, error: membershipsErr }] = await Promise.all([
        supabase.from('membership_type_periods').select('id, membership_type_id, duration_months, price_per_month, price_per_period, vat_rate, available_from, available_until, payment_frequency, period_discount_pct, is_active, promo_label'),
        supabase.from('client_memberships').select('membership_type_id').eq('status', 'active'),
      ]);
      if (periodsErr) throw periodsErr;
      if (membershipsErr) throw membershipsErr;
      setPeriodsByType((periods || []).reduce((acc, period) => {
        (acc[period.membership_type_id] ||= []).push(period);
        return acc;
      }, {}));
      setActiveMemberCounts((memberships || []).reduce((acc, membership) => {
        acc[membership.membership_type_id] = (acc[membership.membership_type_id] || 0) + 1;
        return acc;
      }, {}));
    } catch (err) {
      setError(t('abonnementen.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  function duplicateType(type) {
    setTypeModal({ ...type, id: undefined, name: t('abonnementen.kopieNaam', { naam: type.name }), _duplicateFromId: type.id });
  }

  async function toggleType() {
    const type = toggleConfirm;
    setToggleConfirm(null);
    if (!type) return;
    try {
      const { data: toggleRow, error: toggleErr } = await supabase
        .from('membership_types')
        .update({ is_active: !type.is_active })
        .eq('id', type.id)
        .select('id')
        .maybeSingle();
      if (toggleErr) throw toggleErr;
      if (!toggleRow) { setError(t('common.wijzigingNietOpgeslagen')); return; }
      setAbonnementen(current => current.map(item => item.id === type.id ? { ...item, is_active: !item.is_active } : item));
    } catch (toggleErr) {
      setError(t('abonnementen.foutStatusWijzigen') + toggleErr.message);
    }
  }

  const visibleSubscriptions = abonnementen.filter(type => {
    if (!toonInactief && !type.is_active) return false;
    if (visibilityFilter === 'client' && !type.client_selectable) return false;
    if (visibilityFilter === 'coach' && type.client_selectable) return false;
    const query = search.trim().toLocaleLowerCase('nl-NL');
    return !query || `${type.name} ${type.description || ''}`.toLocaleLowerCase('nl-NL').includes(query);
  });
  const activeCount = abonnementen.filter(type => type.is_active).length;
  const clientVisibleCount = abonnementen.filter(type => type.is_active && type.client_selectable).length;
  const attentionCount = abonnementen.filter(type => type.is_active && (
    !type.agreement_text?.trim() || !(periodsByType[type.id] || []).some(period => period.is_active && !isStaffelVerstreken(period))
  )).length;

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ marginBottom: 6 }}>
            <Link to="/beheer/organisatie/bedrijf" className="text-muted" style={{ fontSize: 13 }}>
              ← {t('beheer.organisatieTitel')}
            </Link>
          </div>
          <h1 className="page-title">{t('abonnementen.title')}</h1>
          <p className="page-subtitle">{t('abonnementen.subtitle')}</p>
        </div>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      <section className="membership-overview" aria-label={t('abonnementen.overzichtTitel')}>
        <div><strong>{activeCount}</strong><span>{t('abonnementen.actieveTypes')}</span></div>
        <div><strong>{clientVisibleCount}</strong><span>{t('abonnementen.zichtbaarVoorKlanten')}</span></div>
        <div className={attentionCount ? 'needs-attention' : ''}><strong>{attentionCount}</strong><span>{t('abonnementen.aandachtspunten')}</span></div>
      </section>

      <div className="membership-toolbar">
        <input className="form-input" type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('abonnementen.zoekenPlaceholder')} aria-label={t('abonnementen.zoekenLabel')} />
        <select className="form-input" value={visibilityFilter} onChange={e => setVisibilityFilter(e.target.value)} aria-label={t('abonnementen.zichtbaarheidFilter')}>
          <option value="all">{t('abonnementen.filterAlle')}</option>
          <option value="client">{t('abonnementen.filterClient')}</option>
          <option value="coach">{t('abonnementen.filterCoach')}</option>
        </select>
        <label className="membership-inactive-toggle" htmlFor="toon_inactief">
          <input type="checkbox" id="toon_inactief" checked={toonInactief} onChange={e => setToonInactief(e.target.checked)} />
          {t('abonnementen.toonInactief')}
        </label>
      </div>

      <div className="membership-model-guide" aria-label={t('abonnementen.modelUitlegTitel')}>
        <span><strong>{t('abonnementen.modelPakket')}</strong>{t('abonnementen.modelPakketUitleg')}</span>
        <span><strong>{t('abonnementen.modelPrijs')}</strong>{t('abonnementen.modelPrijsUitleg')}</span>
        <span><strong>{t('abonnementen.modelLidmaatschap')}</strong>{t('abonnementen.modelLidmaatschapUitleg')}</span>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          {SESSION_SECTIONS.map(section => {
            const typesInSection = visibleSubscriptions.filter(a => a.session_type === section.key);
            const isCollapsed = !!collapsedSections[section.key];
            return (
              <div key={section.key}>
                {/* Sectie header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '14px 20px',
                  background: section.bg,
                  border: `1px solid ${section.border}`,
                  borderLeft: `4px solid ${section.color}`,
                  borderRadius: 8,
                  marginBottom: 12,
                  flexWrap: 'wrap',
                }}>
                  <button
                    className="membership-section-toggle"
                    type="button"
                    onClick={() => setCollapsedSections(current => ({ ...current, [section.key]: !current[section.key] }))}
                    aria-expanded={!isCollapsed}
                    aria-label={t(isCollapsed ? 'abonnementen.sectieUitklappen' : 'abonnementen.sectieInklappen', { naam: section.label() })}
                  >
                    {isCollapsed ? '▶' : '▼'}
                  </button>
                  <span style={{
                    background: section.color,
                    color: 'white',
                    borderRadius: 6,
                    padding: '3px 10px',
                    fontWeight: 700,
                    fontSize: 13,
                    letterSpacing: '0.03em',
                    flexShrink: 0,
                  }}>
                    {section.badge}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: section.color }}>
                      {section.label()}
                    </div>
                    <div style={{ fontSize: 12, color: section.color, opacity: 0.75, marginTop: 2 }}>
                      {section.hint()}
                    </div>
                  </div>
                  <button
                    className="btn btn-sm"
                    style={{ background: section.color, color: 'white', border: 'none', flexShrink: 0 }}
                    onClick={() => setTypeModal({ _defaultSessionType: section.key })}
                  >
                    {t('abonnementen.sectionNieuwVoorType')}
                  </button>
                </div>

                {/* Tabel met types of lege staat */}
                {!isCollapsed && (typesInSection.length === 0 ? (
                  <div style={{
                    padding: '20px 24px',
                    background: 'var(--color-surface)',
                    border: '1px dashed var(--color-border)',
                    borderRadius: 8,
                    textAlign: 'center',
                    color: 'var(--color-text-muted)',
                    fontSize: 14,
                  }}>
                    {search.trim() || visibilityFilter !== 'all'
                      ? t('abonnementen.geenFilterResultaten')
                      : <>{t('abonnementen.sectionGeenTypes')}{' '}<button className="btn btn-ghost btn-sm" style={{ color: section.color, fontSize: 13, textDecoration: 'underline' }} onClick={() => setTypeModal({ _defaultSessionType: section.key })}>{t('abonnementen.sectionNieuwVoorType')}</button></>}
                  </div>
                ) : (
                  <div className="card" style={{ padding: 0 }}>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>{t('abonnementen.colNaam')}</th>
                            <th>{t('abonnementen.colSessiesPerWeek')}</th>
                            <th>{t('abonnementen.colKlantprijs')}</th>
                            <th>{t('abonnementen.colStatus')}</th>
                            <th style={{ textAlign: 'right' }}>{t('abonnementen.colActies')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {typesInSection.map(ab => (
                            <StaffelsRij
                              key={ab.id}
                              type={ab}
                              toonInactief={toonInactief}
                              allTypes={abonnementen}
                              summaryPeriods={periodsByType[ab.id] || []}
                              activeMembers={activeMemberCounts[ab.id] || 0}
                              onDuplicate={duplicateType}
                              onToggleType={setToggleConfirm}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {typeModal !== null && (
        <TypeModal
          abonnement={typeModal?.id ? typeModal : null}
          allTypes={abonnementen}
          defaultSessionType={typeModal?._defaultSessionType || 'pt'}
          duplicateFromId={typeModal?._duplicateFromId}
          onClose={() => setTypeModal(null)}
          onSaved={() => { setTypeModal(null); load(); }}
        />
      )}

      {toggleConfirm && (
        <ConfirmModal
          title={t(toggleConfirm.is_active ? 'abonnementen.deactiverenTitel' : 'abonnementen.activerenTitel')}
          body={toggleConfirm.is_active
            ? t((activeMemberCounts[toggleConfirm.id] || 0) === 1 ? 'abonnementen.deactiverenImpactEen' : 'abonnementen.deactiverenImpactMeerdere', { naam: toggleConfirm.name, aantal: activeMemberCounts[toggleConfirm.id] || 0 })
            : t('abonnementen.activerenImpact', { naam: toggleConfirm.name })}
          onConfirm={toggleType}
          onClose={() => setToggleConfirm(null)}
          danger={toggleConfirm.is_active}
        />
      )}
    </div>
  );
}
